export type ReleaseChange = {
  id?: number;
  description?: string | null;
};

export type ReleaseHistoryItem = {
  id?: number;
  releaseDate?: string;
  frontendVersion?: string | null;
  backendVersion?: string | null;
  changes?: ReleaseChange[];
};

export type ReleaseChangeFormRow = {
  localId: number;
  description: string;
};

export type ReleaseWriteForm = {
  releaseDate: string; // yyyy-MM-dd
  frontendVersion: string;
  backendVersion: string;
  changes: ReleaseChangeFormRow[];
};

export function summarizeReleaseFeatures(
  changes: ReleaseChange[] | null | undefined,
  maxLen = 80
): string {
  const parts = (changes ?? [])
    .map((c) => (c.description ?? "").trim())
    .filter(Boolean);
  if (parts.length === 0) return "—";
  const joined = parts.join("; ");
  if (joined.length <= maxLen) return joined;
  return `${joined.slice(0, Math.max(0, maxLen - 1))}…`;
}

export function toReleaseWritePayload(form: ReleaseWriteForm) {
  const fe = form.frontendVersion.trim();
  const be = form.backendVersion.trim();
  return {
    releaseDate: form.releaseDate,
    frontendVersion: fe.length ? fe : null,
    backendVersion: be.length ? be : null,
    changes: form.changes
      .map((c) => ({ description: c.description.trim() }))
      .filter((c) => c.description.length > 0),
  };
}

export function releaseHasRequiredVersions(
  form: Pick<ReleaseWriteForm, "frontendVersion" | "backendVersion">
): boolean {
  return form.frontendVersion.trim().length > 0 || form.backendVersion.trim().length > 0;
}

export function isReleaseFormDirty(
  form: ReleaseWriteForm,
  baseline: ReleaseWriteForm
): boolean {
  if (form.releaseDate !== baseline.releaseDate) return true;
  if (form.frontendVersion !== baseline.frontendVersion) return true;
  if (form.backendVersion !== baseline.backendVersion) return true;
  if (form.changes.length !== baseline.changes.length) return true;
  return form.changes.some(
    (c, i) => c.description !== baseline.changes[i]?.description
  );
}

export function toDateInputValue(isoOrDate?: string | null): string {
  if (!isoOrDate) return "";
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) {
    return String(isoOrDate).slice(0, 10);
  }
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function todayDateInputValue(): string {
  return toDateInputValue(new Date().toISOString());
}

export function formatReleaseDate(isoOrDate?: string | null): string {
  const value = toDateInputValue(isoOrDate);
  return value || "—";
}
